import { maybeJumpstartPlayback } from '@hotjar/webapp/features/Playbacks/utils/maybeJumpstartPlayback';
import { getParameterByName } from '@hotjar/webapp/features/Playbacks/store/utils';
import { event as HJEvent } from '@hotjar/tracking/src/tools/hotjar';
import { Store } from '../../../store/player/types';
import { shouldShowSecureModal } from './shouldShowSecureModal';
import { jumpTo } from './jumpTo';
import { play } from './play';
import { pause } from './pause';
import type { EventMarker } from '@hotjar/webapp/features/Playbacks/types';

export const startRecording = (store: Store): void => {
  const {
    autostart,
    isHighlightMode,
    filterEvents,
    isScreenshotMode,
    playbackScript,
    isHtmlReportMode,
    openedCommentId,
    isJumpStartActive,
    jumpStartEvent,
    setLoaded,
    setPlaying,
    openConsole,
  } = store.getState();

  // Automatically start the playback if we are not on a public recording and
  // once the player has loaded
  if (autostart && !shouldShowSecureModal() && !openedCommentId) {
    const queryParamTime = getParameterByName('startTime') || 0;

    if (queryParamTime > 0) {
      if (!isHighlightMode) {
        setPlaying(true);
      }
      jumpTo(store, +queryParamTime);
    } else {
      if (!isHighlightMode) {
        play(store);
      }
    }
  }

  setLoaded();
  if (isJumpStartActive) {
    const isJumpStarting = maybeJumpstartPlayback(jumpStartEvent, jumpTo.bind(null, store));
    /*
      Since we can have multiple highlighted events we evaluate isJumpStartingOnError as true,
      if and only if the error event is the first one happening.
    */
    const isJumpStartingOnError =
      isJumpStarting && filterEvents.findIndex((event) => event.type === 'error') === 0;

    if (isJumpStartingOnError) {
      // The jump start is happening with a 5 seconds gap before the actual event.
      // In this case we don't apply the gap, since it could highlight a different error.
      const firstErrorEvent = filterEvents.find((event) => event.type === 'error') as EventMarker;
      openConsole(firstErrorEvent.time);
      HJEvent('Console Opened');
    }
  }

  window.dispatchEvent(new CustomEvent('recording-loaded'));

  /**
   * This branch of code is the entry point that handles recording screenshots (related ticket: AT-743).
   *
   * ## Key participants
   *
   * - RecordingScreenshotter, the backend component and main orchestrator.
   * - Player, the recording player UI + controller in the frontend.
   * - PlaybackScript, the script which actually executes the playback (mutations etc) in a separate
   *   IFrame.
   *
   * The RecordingScreenshotter communicates with the Player through Selenium.
   * The Player communicates with the Playback script through postMessage / addEventListener.
   *
   * ## Sequence of events
   *
   * 1. RecordingScreenshotter starts a browser and opens the Player with
   *    ?forScreenshot=1&startTime=10000. The actual startTime value is decided by the backend and will
   *    vary.
   *
   * 2. The Player adds an event listener to listen for messages from the playback script.
   *    It listens for the events 'eventsProcessed' and 'retrieveHtmlResponse' (more on this later).
   *
   * 3. The Player + PlaybackScript starts processing events to reach the desired startTime.
   *    It essentially fast-forwards the recording to the right time.
   *
   * 4. PlaybackScript emits an event called 'eventsProcessed' for every batch of events it processes.
   *
   * 5. The Player eventually sees that we have processed all the events we need to by looking
   *    at the data included in the 'eventsProcessed' event. At that point it issues the 'retrieveHtml'
   *    command to the Playback script.
   *
   * 6. Once the PlaybackScript receives the 'retrieveHtml' response it serializes the current page
   *    content and posts a 'retrieveHtmlResponse' message with the page content as data.
   *
   * 7. The Player stores the page content in a local variable and creates a function called
   *    `window._hjReplaceDocument()` and sets `window._hjContentLoaded = true`.
   *
   * 8. As soon as the RecordingScreenshotter sees that `_hjContentLoaded == true` it calls the
   *    `_hjReplaceDocument()` function to "hide" the recording player and just display the actual
   *    page content instead.
   *
   * 9. The RecordingScreenshotter continues to call a few more JS functions to set up the page
   *    in the desired state, get the element map etc. Eventually it takes the screenshot and we're done!
   */
  if (isScreenshotMode) {
    const pauseRecording = () => {
      // TODO: Revisit this when a safeguard is in place.
      // pause calls were not taking effect, and agreed to go with a workaround for now.
      // "please go with the setTimeout for now, the issue is that you're pausing in the middle of event processing, so the next tick is queued anyway".
      setTimeout(() => {
        pause(store);
      }, 0);
    };

    // The playback script has processed a bunch of events.
    playbackScript.onInactiveTickDone(() => {
      playbackScript.retrieveHtml();
      pauseRecording();
    });

    // The playback script has provided us with the HTML of its' page.
    playbackScript.onRetrieveHtmlResponse((data) => {
      // Store the html and create a function which will be called through Selenium
      // once it detects that the page itself has loaded and `_hjContentLoaded`
      // evaluates to true.
      //
      // It's important to let Selenium call this function instead of just
      // executing it on the spot. Doing so before Selenium has realised that the page
      // has loaded actually causes Selenium to think it never loads, and therefore
      // causes a timeout.
      const pageHtml = data;
      window._hjReplaceDocument = function () {
        // nosemgrep
        document.write(pageHtml);
      };

      window._hjReplaceDocumentV2 = function () {
        const iframe: HTMLIFrameElement | null = document.querySelector('#content-iframes .active');
        if (!iframe || !iframe.contentDocument) return;

        const parentNode = iframe.contentDocument.body.parentNode as HTMLElement;
        const windowParentNode = window.document.body.parentNode as HTMLElement;

        [...parentNode.attributes].forEach((attr) => {
          windowParentNode.setAttribute(attr.nodeName, attr.nodeValue || '');
        });
        window.document.head.replaceWith(iframe.contentDocument.head);
        window.document.body.replaceWith(iframe.contentDocument.body);
      };

      window._hjContentLoaded = true;

      if (isHtmlReportMode) {
        window.parent.postMessage(
          {
            event: 'html_background_data',
            payload: pageHtml,
          },
          window.location.origin,
        );
      }
    });

    playbackScript.onEventsProcessed(({ page, toPosition }) => {
      const { currentPageIndex, currentPosition } = store.getState();

      // Get the page and event position within that page where we want to be.
      // We might not be there yet because all events might not have been processed.
      // Ask the playback script for the page HTML if we've processed all the events.
      if (page === currentPageIndex && toPosition === currentPosition) {
        playbackScript.retrieveHtml();
        pauseRecording();
      }
    });
  }
};
